import {
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Download,
    Eye,
    ExternalLink,
    Loader2,
    RefreshCw,
    Search,
    SlidersHorizontal,
    Users,
    X
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { tenantService, venueService, workerService } from '../services/api';
import './AdminWorkers.css';

const CONNECT_STATUS_OPTIONS = [
    { value: '', label: 'Stato Connect (tutti)' },
    { value: 'CONNECTED', label: 'Connesso' },
    { value: 'ONBOARDING_PENDING', label: 'Onboarding' },
    { value: 'INVITED', label: 'Invitato' },
];

const WORKER_STATUS_OPTIONS = [
    { value: '', label: 'Stato worker (tutti)' },
    { value: 'ACTIVE', label: 'Attivo' },
    { value: 'INACTIVE', label: 'Inattivo' },
];

const ACTIVITY_OPTIONS = [
    { value: '', label: 'Attività (tutte)' },
    { value: 'WITH_ACTIVITY', label: 'Con attività' },
    { value: 'WITHOUT_ACTIVITY', label: 'Senza attività' },
];

const COST_OPTIONS = [
    { value: '', label: 'Costi Connect (tutti)' },
    { value: 'WITH_CONNECT_COSTS', label: 'Con costi Connect' },
    { value: 'WITHOUT_CONNECT_COSTS', label: 'Senza costi Connect' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_BY = 'totalConnectFee';
const DEFAULT_SORT_DIRECTION = 'desc';

const SORT_DEFAULT_DIRECTIONS = {
    totalConnectFee: 'desc',
    anomalies: 'desc',
    lastActivityAt: 'desc',
    tipCount: 'desc',
    workerNetAmount: 'desc',
    payoutVolume: 'desc',
    workerName: 'asc',
    tenantName: 'asc',
    connectStatus: 'asc',
};

const FILTER_LABELS = {
    yearMonth: 'Mese',
    search: 'Ricerca worker',
    tenantId: 'Tenant',
    venueId: 'Venue',
    workerStatus: 'Stato worker',
    connectStatus: 'Stato Connect',
    activityStatus: 'Attività',
    connectCostStatus: 'Costi Connect',
    paymentIssues: 'Solo payment issues',
};

const ISSUE_LABELS = {
    WORKER_NOT_CONNECTED: 'Worker non connesso',
    WORKER_ONBOARDING_PENDING: 'Onboarding Connect in corso',
    STRIPE_PROVIDER_ISSUES: 'Problemi dati Stripe',
};

const CONNECT_STATUS_LABELS = {
    CONNECTED: 'Connesso',
    ONBOARDING_PENDING: 'Onboarding',
    INVITED: 'Invitato',
};

const WORKER_STATUS_LABELS = {
    ACTIVE: 'Attivo',
    INACTIVE: 'Inattivo',
};

const CONNECT_DATA_STATE_META = {
    AVAILABLE: { label: 'Disponibile', className: 'aw-badge-ok', title: 'Dato Stripe disponibile per il mese selezionato' },
    SYNC_RUNNING: { label: 'Sync in corso', className: 'aw-badge-running', title: 'Sincronizzazione Stripe in corso' },
    SYNC_FAILED: { label: 'Sync fallito', className: 'aw-badge-error', title: 'L’ultimo tentativo di sincronizzazione è fallito' },
    NO_ACCOUNT: { label: 'Senza Connected Account', className: 'aw-badge-muted', title: 'Worker senza Connected Account collegato' },
    NOT_AVAILABLE: { label: 'Dato non sincronizzato', className: 'aw-badge-muted', title: 'Il report del mese non è ancora disponibile' },
};

const FILTER_IDS = {
    yearMonth: 'aw-year-month',
    search: 'aw-search',
    tenantId: 'aw-tenant',
    venueId: 'aw-venue',
    workerStatus: 'aw-worker-status',
    connectStatus: 'aw-connect-status',
    activityStatus: 'aw-activity-status',
    connectCostStatus: 'aw-connect-cost-status',
    paymentIssues: 'aw-payment-issues',
};

const EMPTY_SYNC_STATUS = {};

export function getCurrentYearMonth(now = new Date()) {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function getLastClosedYearMonth(now = new Date()) {
    return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1))
        .toISOString()
        .slice(0, 7);
}

export function isClosedYearMonth(yearMonth, now = new Date()) {
    return Boolean(yearMonth) && yearMonth < getCurrentYearMonth(now);
}

function formatCurrencyFromCents(cents) {
    if (cents == null) return '—';
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
    }).format(Number(cents) / 100);
}

function formatNumber(value) {
    if (value == null) return '—';
    return new Intl.NumberFormat('it-IT').format(Number(value));
}

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('it-IT');
}

function formatShortDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('it-IT');
}

function formatCurrencyForExcel(cents) {
    if (cents == null) return '';
    return Number(cents) / 100;
}

function maskAccountId(value) {
    if (!value) return '—';
    const text = String(value);
    if (text.length <= 10) return text;
    return `${text.slice(0, 10)}…${text.slice(-4)}`;
}

function getOptionLabel(options, value) {
    return options.find((option) => option.value === value)?.label || value || '—';
}

function getConnectStatusLabel(status) {
    return CONNECT_STATUS_LABELS[status] || '—';
}

function getWorkerStatusLabel(status) {
    return WORKER_STATUS_LABELS[status] || '—';
}

function ConnectStatusBadge({ status }) {
    const label = getConnectStatusLabel(status);
    const className = status === 'CONNECTED'
        ? 'aw-badge aw-badge-ok'
        : status === 'ONBOARDING_PENDING'
            ? 'aw-badge aw-badge-running'
            : status === 'INVITED'
                ? 'aw-badge aw-badge-muted'
                : 'aw-badge aw-badge-muted';

    return <span className={className}>{label}</span>;
}

function WorkerStatusBadge({ status }) {
    const label = getWorkerStatusLabel(status);
    const className = status === 'ACTIVE' ? 'aw-badge aw-badge-ok' : 'aw-badge aw-badge-muted';
    return <span className={className}>{label}</span>;
}

function ConnectDataStateBadge({ state }) {
    const meta = CONNECT_DATA_STATE_META[state] || CONNECT_DATA_STATE_META.NOT_AVAILABLE;
    return (
        <span className={`aw-badge ${meta.className}`} title={meta.title} aria-label={meta.title}>
            {meta.label}
        </span>
    );
}

function IssueBadge({ issues = [] }) {
    if (!issues.length) {
        return <span className="aw-badge aw-badge-ok">Nessuna</span>;
    }

    const label = issues.length === 1 ? '1 anomalia' : `${issues.length} anomalie`;
    const title = issues.map((issue) => ISSUE_LABELS[issue] || issue).join('\n');
    return (
        <span className="aw-badge aw-badge-error" title={title} aria-label={title}>
            {label}
        </span>
    );
}

function KpiCard({ label, value, subValue, title }) {
    return (
        <div className="aw-kpi-card" title={title}>
            <p className="aw-kpi-label">{label}</p>
            <p className="aw-kpi-value">{value}</p>
            {subValue ? <p className="aw-kpi-subvalue">{subValue}</p> : null}
        </div>
    );
}

function SortableHeader({ label, field, sortBy, sortDirection, onSort, alignRight = false }) {
    const active = sortBy === field;
    return (
        <th className={`${alignRight ? 'aw-th-right' : ''} aw-sortable-th`} aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button
                type="button"
                className={`aw-th-sort-btn${active ? ' aw-th-sort-btn-active' : ''}`}
                onClick={() => onSort(field)}
                aria-label={`Ordina per ${label}`}
            >
                <span>{label}</span>
                {active ? (sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronDown size={12} className="aw-sort-idle" />}
            </button>
        </th>
    );
}

function DrawerMetric({ label, value, subValue, title }) {
    return (
        <div className="aw-drawer-metric" title={title}>
            <span className="aw-drawer-metric-label">{label}</span>
            <strong className="aw-drawer-metric-value">{value}</strong>
            {subValue ? <span className="aw-drawer-metric-subvalue">{subValue}</span> : null}
        </div>
    );
}

function getSyncBannerMeta(syncStatus = {}, yearMonth) {
    const lastAttempt = formatDate(syncStatus.lastSyncedAt);
    const status = syncStatus.status || 'NOT_AVAILABLE';

    if (!isClosedYearMonth(yearMonth)) {
        return {
            className: 'aw-banner-warning',
            title: 'Mese non ancora concluso',
            body: `I costi Connect di ${yearMonth} non sono ancora consolidati da Stripe`,
            detail: `Seleziona ${getLastClosedYearMonth()} o un mese precedente per avviare la sincronizzazione.`,
        };
    }

    if (status === 'FAILED') {
        return {
            className: 'aw-banner-error',
            title: 'Sincronizzazione fallita',
            body: `Mese ${yearMonth} · ultimo tentativo ${lastAttempt}`,
            detail: syncStatus.errorMessage || 'Dettaglio errore non disponibile',
        };
    }

    if (status === 'NOT_AVAILABLE') {
        const reportUnavailable = syncStatus.reasonCode === 'CONNECT_COST_REPORT_UNAVAILABLE';
        return {
            className: 'aw-banner-warning',
            title: reportUnavailable
                ? 'Report costi Connect non disponibile'
                : syncStatus.latestRunId ? 'Report non disponibile' : 'Dato del mese non ancora sincronizzato',
            body: syncStatus.latestRunId
                ? `Mese ${yearMonth} · ultimo tentativo ${lastAttempt}`
                : `Mese ${yearMonth} · nessuna sincronizzazione disponibile`,
            detail: reportUnavailable
                ? 'L’account Stripe configurato non espone un report mensile con tutte le colonne necessarie per i costi Connect. In ambiente TEST questa disponibilità può differire dal LIVE.'
                : syncStatus.errorMessage || 'Il report Stripe non è ancora disponibile per questo mese',
        };
    }

    if (status === 'RUNNING') {
        return {
            className: 'aw-banner-running',
            title: 'Sincronizzazione in corso',
            body: `Mese ${yearMonth} · avviata alle ${lastAttempt}`,
            detail: syncStatus.errorMessage || 'La sincronizzazione Stripe è in corso',
        };
    }

    return null;
}

function buildExportRows(payloadRows = [], yearMonth) {
    return payloadRows.map((row) => [
        row.workerName || '—',
        row.workerEmail || '—',
        row.tenantName || '—',
        row.venueName || '—',
        getWorkerStatusLabel(row.workerStatus),
        getConnectStatusLabel(row.connectStatus),
        row.providerAccountId || '—',
        row.activity?.distinctTipsCount ?? 0,
        row.activity?.transferCount ?? 0,
        formatCurrencyForExcel(row.activity?.netAmountCents),
        formatDate(row.activity?.lastActivityAt),
        (row.paymentIssues || []).map((issue) => ISSUE_LABELS[issue] || issue).join(', ') || 'Nessuna',
        row.stripeConnectCost?.numberOfPayouts ?? '',
        formatCurrencyForExcel(row.stripeConnectCost?.payoutVolumeCents),
        formatCurrencyForExcel(row.stripeConnectCost?.activeAccountBillingCents),
        formatCurrencyForExcel(row.stripeConnectCost?.accountVolumeBillingCents),
        formatCurrencyForExcel(row.stripeConnectCost?.payoutFeeCents),
        formatCurrencyForExcel(row.stripeConnectCost?.verificationBillingCents),
        formatCurrencyForExcel(row.stripeConnectCost?.totalConnectFeeCents),
        CONNECT_DATA_STATE_META[row.stripeConnectCost?.dataState]?.label || 'Dato non sincronizzato',
        formatDate(row.stripeConnectCost?.lastSyncedAt),
        yearMonth,
    ]);
}

function buildExportWorkbook(payload, generatedAtIso) {
    const headers = [
        'Worker',
        'Email',
        'Tenant',
        'Venue',
        'Stato worker',
        'Stato Connect',
        'Connected Account',
        'Numero mance',
        'Quote ricevute',
        'Netto worker',
        'Ultima attività',
        'Payment issues',
        'Numero payout',
        'Volume payout',
        'Active Account Billing',
        'Account Volume Billing',
        'Payout Fee',
        'Verification Billing',
        'Costi Connect totali',
        'Stato dato Stripe',
        'Ultima sincronizzazione',
        'Mese di competenza',
    ];

    const metaRows = [
        ['Mese di competenza', payload.yearMonth || '—'],
        ['Data e ora generazione', new Date(generatedAtIso).toLocaleString('it-IT')],
        ['Export troncato', payload.truncated ? 'Sì' : 'No'],
        ['Total rows', payload.totalRows],
        ['Exported rows', payload.exportedRows],
        ['Max export rows', payload.maxExportRows],
        [''],
    ];

    const worksheetRows = [
        ...metaRows,
        headers,
        ...buildExportRows(payload.rows || [], payload.yearMonth || '—'),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows) || {};
    worksheet['!cols'] = [
        { wch: 24 },
        { wch: 30 },
        { wch: 28 },
        { wch: 28 },
        { wch: 14 },
        { wch: 16 },
        { wch: 18 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 20 },
        { wch: 24 },
        { wch: 12 },
        { wch: 14 },
        { wch: 16 },
        { wch: 18 },
        { wch: 12 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new() || {};
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Admin Workers');
    return workbook;
}

export default function AdminWorkers() {
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [exportMessage, setExportMessage] = useState(null);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [showSyncDetail, setShowSyncDetail] = useState(false);
    const [selectedWorker, setSelectedWorker] = useState(null);

    const [data, setData] = useState({ workers: [], kpis: {}, pagination: {}, syncStatus: {} });
    const [tenants, setTenants] = useState([]);
    const [venues, setVenues] = useState([]);

    const closeButtonRef = useRef(null);
    const restoreFocusRef = useRef(null);

    const [filters, setFilters] = useState({
        yearMonth: getLastClosedYearMonth(),
        search: '',
        tenantId: '',
        venueId: '',
        workerStatus: '',
        connectStatus: '',
        activityStatus: '',
        connectCostStatus: '',
        paymentIssues: false,
        page: 1,
        limit: DEFAULT_PAGE_SIZE,
        sortBy: DEFAULT_SORT_BY,
        sortDirection: DEFAULT_SORT_DIRECTION,
    });

    const loadTenants = useCallback(async () => {
        try {
            const tenantData = await tenantService.getTenants();
            const normalized = Array.isArray(tenantData)
                ? tenantData
                : Array.isArray(tenantData?.data)
                    ? tenantData.data
                    : Array.isArray(tenantData?.tenants)
                        ? tenantData.tenants
                        : [];
            setTenants(normalized);
        } catch (err) {
            console.error('Failed to load tenants', err);
        }
    }, []);

    const loadVenues = useCallback(async (tenantId) => {
        try {
            const response = await venueService.getVenues({ tenantId, limit: 1000 });
            const normalized = response?.venues || response?.data || [];
            setVenues(normalized);
        } catch (err) {
            console.error('Failed to load venues', err);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        setExportMessage(null);

        try {
            const params = {
                yearMonth: filters.yearMonth,
                search: filters.search || undefined,
                tenantId: filters.tenantId || undefined,
                venueId: filters.venueId || undefined,
                workerStatus: filters.workerStatus || undefined,
                connectStatus: filters.connectStatus || undefined,
                activityStatus: filters.activityStatus || undefined,
                connectCostStatus: filters.connectCostStatus || undefined,
                paymentIssues: filters.paymentIssues || undefined,
                page: filters.page,
                limit: filters.limit,
                sortBy: filters.sortBy,
                sortDirection: filters.sortDirection,
            };

            const response = await workerService.getMonthlyInsights(params);
            setData(response);
        } catch (err) {
            const message = err.response?.data?.error || err.message || 'Errore caricamento pagina Workers';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadTenants();
    }, [loadTenants]);

    useEffect(() => {
        loadVenues(filters.tenantId);
    }, [filters.tenantId, loadVenues]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (!selectedWorker) return undefined;

        restoreFocusRef.current = document.activeElement;
        const timeout = window.setTimeout(() => {
            closeButtonRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [selectedWorker]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && selectedWorker) {
                setSelectedWorker(null);
            }
        };

        if (selectedWorker) {
            document.addEventListener('keydown', onKeyDown);
        }

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [selectedWorker]);

    useEffect(() => {
        if (!selectedWorker && restoreFocusRef.current instanceof HTMLElement) {
            restoreFocusRef.current.focus();
        }
    }, [selectedWorker]);

    const syncStatus = data?.syncStatus ?? EMPTY_SYNC_STATUS;
    const kpis = data?.kpis || {};
    const workers = data?.workers || [];
    const pagination = data?.pagination || { total: 0, totalPages: 1, page: 1, limit: filters.limit };
    const sortBy = data?.sortBy || filters.sortBy;
    const sortDirection = data?.sortDirection || filters.sortDirection;
    const currentPage = pagination.page || 1;
    const pageSize = pagination.limit || filters.limit;
    const totalRows = pagination.total || 0;
    const startRow = totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
    const endRow = totalRows === 0 ? 0 : Math.min(totalRows, currentPage * pageSize);

    const syncBanner = useMemo(() => getSyncBannerMeta(syncStatus, filters.yearMonth), [syncStatus, filters.yearMonth]);

    const advancedFilterCount = useMemo(() => [
        filters.venueId,
        filters.workerStatus,
        filters.connectStatus,
        filters.activityStatus,
        filters.connectCostStatus,
        filters.paymentIssues,
    ].filter((value) => value !== '' && value !== false && value != null).length, [filters]);

    const activeFilterChips = useMemo(() => {
        const chips = [
            { key: 'yearMonth', label: `${FILTER_LABELS.yearMonth}: ${filters.yearMonth}`, clearable: false },
            filters.search ? { key: 'search', label: `${FILTER_LABELS.search}: ${filters.search}` } : null,
            filters.tenantId ? {
                key: 'tenantId',
                label: `${FILTER_LABELS.tenantId}: ${tenants.find((tenant) => tenant.id === filters.tenantId)?.company_name || filters.tenantId}`,
            } : null,
            filters.venueId ? {
                key: 'venueId',
                label: `${FILTER_LABELS.venueId}: ${venues.find((venue) => venue.id === filters.venueId)?.name || filters.venueId}`,
            } : null,
            filters.workerStatus ? { key: 'workerStatus', label: `${FILTER_LABELS.workerStatus}: ${getOptionLabel(WORKER_STATUS_OPTIONS, filters.workerStatus)}` } : null,
            filters.connectStatus ? { key: 'connectStatus', label: `${FILTER_LABELS.connectStatus}: ${getOptionLabel(CONNECT_STATUS_OPTIONS, filters.connectStatus)}` } : null,
            filters.activityStatus ? { key: 'activityStatus', label: `${FILTER_LABELS.activityStatus}: ${getOptionLabel(ACTIVITY_OPTIONS, filters.activityStatus)}` } : null,
            filters.connectCostStatus ? { key: 'connectCostStatus', label: `${FILTER_LABELS.connectCostStatus}: ${getOptionLabel(COST_OPTIONS, filters.connectCostStatus)}` } : null,
            filters.paymentIssues ? { key: 'paymentIssues', label: FILTER_LABELS.paymentIssues } : null,
        ];

        return chips.filter(Boolean);
    }, [filters, tenants, venues]);

    const setFilter = useCallback((name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value,
            page: 1,
        }));
    }, []);

    const clearFilter = useCallback((name) => {
        const defaults = {
            yearMonth: getLastClosedYearMonth(),
            search: '',
            tenantId: '',
            venueId: '',
            workerStatus: '',
            connectStatus: '',
            activityStatus: '',
            connectCostStatus: '',
            paymentIssues: false,
            page: 1,
            limit: DEFAULT_PAGE_SIZE,
            sortBy: DEFAULT_SORT_BY,
            sortDirection: DEFAULT_SORT_DIRECTION,
        };

        setFilters((prev) => ({
            ...prev,
            [name]: defaults[name],
            ...(name === 'tenantId' ? { venueId: '' } : null),
            page: 1,
        }));
    }, []);

    const clearAllFilters = useCallback(() => {
        setFilters({
            yearMonth: getLastClosedYearMonth(),
            search: '',
            tenantId: '',
            venueId: '',
            workerStatus: '',
            connectStatus: '',
            activityStatus: '',
            connectCostStatus: '',
            paymentIssues: false,
            page: 1,
            limit: DEFAULT_PAGE_SIZE,
            sortBy: DEFAULT_SORT_BY,
            sortDirection: DEFAULT_SORT_DIRECTION,
        });
        setShowAdvancedFilters(false);
    }, []);

    const handleSort = useCallback((field) => {
        setFilters((prev) => {
            const nextDirection = prev.sortBy === field
                ? (prev.sortDirection === 'asc' ? 'desc' : 'asc')
                : (SORT_DEFAULT_DIRECTIONS[field] || 'asc');

            return {
                ...prev,
                sortBy: field,
                sortDirection: nextDirection,
                page: 1,
            };
        });
    }, []);

    const onSyncConnectCosts = async () => {
        if (!isClosedYearMonth(filters.yearMonth)) {
            setError(`I costi Connect sono sincronizzabili solo per mesi conclusi. Seleziona ${getLastClosedYearMonth()} o un mese precedente.`);
            return;
        }

        setSyncing(true);
        setError('');
        setExportMessage(null);
        try {
            const response = await workerService.syncConnectCosts({ yearMonth: filters.yearMonth });
            if (response?.result?.reasonCode === 'CONNECT_COST_REPORT_UNAVAILABLE') {
                setExportMessage({
                    type: 'warning',
                    message: 'Stripe non rende disponibile un report mensile compatibile con i costi Connect per questo account o ambiente.',
                });
            }
            await loadData();
        } catch (err) {
            const code = err.response?.data?.code;
            const message = code === 'MONTH_NOT_CLOSED'
                ? `Il mese selezionato non è ancora concluso. Seleziona ${getLastClosedYearMonth()} o un mese precedente.`
                : err.response?.data?.error || err.message || 'Errore sync Stripe Connect costs';
            setError(message);
        } finally {
            setSyncing(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        setError('');
        setExportMessage(null);

        try {
            const payload = await workerService.exportMonthlyInsights({
                yearMonth: filters.yearMonth,
                search: filters.search || undefined,
                tenantId: filters.tenantId || undefined,
                venueId: filters.venueId || undefined,
                workerStatus: filters.workerStatus || undefined,
                connectStatus: filters.connectStatus || undefined,
                activityStatus: filters.activityStatus || undefined,
                connectCostStatus: filters.connectCostStatus || undefined,
                paymentIssues: filters.paymentIssues || undefined,
                sortBy: filters.sortBy,
                sortDirection: filters.sortDirection,
            });

            const rows = payload?.rows || [];
            if (rows.length === 0) {
                setExportMessage({ type: 'warning', message: 'Nessun risultato disponibile per l’export.' });
                return;
            }

            const workbook = buildExportWorkbook(payload, new Date().toISOString());
            XLSX.writeFile(workbook, `soontip-workers-connect-costs-${payload.yearMonth}.xlsx`);

            setExportMessage(
                payload.truncated
                    ? { type: 'warning', message: `Export generato con ${payload.exportedRows} righe su ${payload.totalRows} totali. Limite massimo: ${payload.maxExportRows}.` }
                    : { type: 'success', message: `Export completato: soontip-workers-connect-costs-${payload.yearMonth}.xlsx` }
            );
        } catch (err) {
            const message = err.response?.data?.error || err.message || 'Export fallito';
            setExportMessage({ type: 'error', message });
        } finally {
            setExporting(false);
        }
    };

    const openDrawer = useCallback((worker) => {
        setSelectedWorker(worker);
    }, []);

    const closeDrawer = useCallback(() => {
        setSelectedWorker(null);
    }, []);

    const noResultsMessage = useMemo(() => {
        if (workers.length > 0) return null;
        const hasActiveFilters = activeFilterChips.length > 1;
        if (hasActiveFilters) return 'Nessun risultato per i filtri selezionati.';
        if (syncStatus.status === 'FAILED') return 'Sincronizzazione fallita per il mese selezionato.';
        if (syncStatus.status === 'NOT_AVAILABLE' && syncStatus.latestRunId) return 'Report del mese non disponibile.';
        return 'Nessun worker disponibile per il mese selezionato.';
    }, [activeFilterChips.length, syncStatus.latestRunId, syncStatus.status, workers.length]);

    const connectCostsSubValue = (
        <>
            <span>Costo medio per account fatturato: {formatCurrencyFromCents(kpis.averageConnectCostPerBilledAccountCents)}</span>
            <span>Active Account Billing totale: {formatCurrencyFromCents(kpis.activeAccountBillingTotalCents)}</span>
        </>
    );

    return (
        <div className="aw-page">
            <div className="aw-header">
                <div>
                    <h1 className="aw-title"><Users size={20} /> Admin Workers</h1>
                    <p className="aw-subtitle">Sintesi operativa dei worker e dettaglio analitico dei costi Stripe Connect per Connected Account.</p>
                </div>
                <div className="aw-header-actions">
                    <button className="aw-btn aw-btn-secondary" onClick={() => loadData()} disabled={loading || syncing || exporting}>
                        {loading ? <Loader2 size={16} className="aw-spin" /> : <RefreshCw size={16} />}
                        Aggiorna pagina
                    </button>
                    <button
                        className="aw-btn aw-btn-primary"
                        onClick={onSyncConnectCosts}
                        disabled={syncing || loading || exporting || !isClosedYearMonth(filters.yearMonth)}
                        title={!isClosedYearMonth(filters.yearMonth) ? 'La sincronizzazione è disponibile solo per mesi conclusi' : undefined}
                    >
                        {syncing ? <Loader2 size={16} className="aw-spin" /> : <RefreshCw size={16} />}
                        Sincronizza dati Stripe
                    </button>
                </div>
            </div>

            {error ? (
                <div className="aw-alert aw-alert-error" role="alert">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            ) : null}

            {exportMessage ? (
                <div className={`aw-alert aw-alert-${exportMessage.type === 'success' ? 'success' : exportMessage.type === 'warning' ? 'warning' : 'error'}`} role="status">
                    <Download size={16} />
                    <span>{exportMessage.message}</span>
                </div>
            ) : null}

            {syncBanner ? (
                <div className={`aw-banner ${syncBanner.className}`} role="alert">
                    <div className="aw-banner-main">
                        <strong>{syncBanner.title}</strong>
                        <span>{syncBanner.body}</span>
                    </div>
                    <div className="aw-banner-actions">
                        {syncBanner.detail ? (
                            <button className="aw-btn aw-btn-secondary aw-btn-compact" onClick={() => setShowSyncDetail((prev) => !prev)}>
                                Mostra dettaglio errore
                            </button>
                        ) : null}
                    </div>
                    {showSyncDetail && syncBanner.detail ? (
                        <div className="aw-banner-detail">{syncBanner.detail}</div>
                    ) : null}
                </div>
            ) : null}

            <div className="aw-filters-shell">
                <div className="aw-filters-main-row">
                    <div className="aw-filter-row">
                        <label htmlFor={FILTER_IDS.yearMonth}>Mese</label>
                        <input
                            id={FILTER_IDS.yearMonth}
                            type="month"
                            value={filters.yearMonth}
                            onChange={(event) => setFilter('yearMonth', event.target.value)}
                        />
                    </div>

                    <div className="aw-filter-row aw-filter-search">
                        <label htmlFor={FILTER_IDS.search}>Ricerca worker</label>
                        <div className="aw-search-box">
                            <Search size={15} />
                            <input
                                id={FILTER_IDS.search}
                                type="text"
                                value={filters.search}
                                placeholder="Nome, cognome, email"
                                onChange={(event) => setFilter('search', event.target.value)}
                            />
                        </div>
                    </div>

                    <div className="aw-filter-row">
                        <label htmlFor={FILTER_IDS.tenantId}>Tenant</label>
                        <select id={FILTER_IDS.tenantId} value={filters.tenantId} onChange={(event) => {
                            const value = event.target.value;
                            setFilters((prev) => ({
                                ...prev,
                                tenantId: value,
                                venueId: '',
                                page: 1,
                            }));
                        }}>
                            <option value="">Tutti i tenant</option>
                            {tenants.map((tenant) => (
                                <option key={tenant.id} value={tenant.id}>{tenant.company_name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="aw-filter-actions">
                        <button
                            className="aw-btn aw-btn-secondary"
                            type="button"
                            onClick={() => setShowAdvancedFilters((prev) => !prev)}
                            aria-expanded={showAdvancedFilters}
                        >
                            <SlidersHorizontal size={15} />
                            Altri filtri
                            {advancedFilterCount > 0 ? <span className="aw-filter-count">{advancedFilterCount}</span> : null}
                        </button>

                        <button
                            className="aw-btn aw-btn-secondary"
                            type="button"
                            onClick={handleExport}
                            disabled={exporting || loading || totalRows === 0}
                        >
                            {exporting ? <Loader2 size={15} className="aw-spin" /> : <Download size={15} />}
                            Esporta Excel
                        </button>

                        <button className="aw-btn aw-btn-ghost" type="button" onClick={clearAllFilters} disabled={loading || syncing || exporting}>
                            <X size={15} />
                            Azzera filtri
                        </button>
                    </div>
                </div>

                {showAdvancedFilters ? (
                    <div className="aw-filters-advanced">
                        <div className="aw-filter-row">
                            <label htmlFor={FILTER_IDS.venueId}>Venue</label>
                            <select id={FILTER_IDS.venueId} value={filters.venueId} onChange={(event) => setFilter('venueId', event.target.value)}>
                                <option value="">Tutte le venue</option>
                                {venues.map((venue) => (
                                    <option key={venue.id} value={venue.id}>{venue.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="aw-filter-row">
                            <label htmlFor={FILTER_IDS.workerStatus}>Stato worker</label>
                            <select id={FILTER_IDS.workerStatus} value={filters.workerStatus} onChange={(event) => setFilter('workerStatus', event.target.value)}>
                                {WORKER_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value || 'all-worker'} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="aw-filter-row">
                            <label htmlFor={FILTER_IDS.connectStatus}>Stato Connect</label>
                            <select id={FILTER_IDS.connectStatus} value={filters.connectStatus} onChange={(event) => setFilter('connectStatus', event.target.value)}>
                                {CONNECT_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value || 'all-connect'} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="aw-filter-row">
                            <label htmlFor={FILTER_IDS.activityStatus}>Attività</label>
                            <select id={FILTER_IDS.activityStatus} value={filters.activityStatus} onChange={(event) => setFilter('activityStatus', event.target.value)}>
                                {ACTIVITY_OPTIONS.map((option) => (
                                    <option key={option.value || 'all-activity'} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="aw-filter-row">
                            <label htmlFor={FILTER_IDS.connectCostStatus}>Disponibilità costi Connect</label>
                            <select id={FILTER_IDS.connectCostStatus} value={filters.connectCostStatus} onChange={(event) => setFilter('connectCostStatus', event.target.value)}>
                                {COST_OPTIONS.map((option) => (
                                    <option key={option.value || 'all-cost'} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="aw-filter-row aw-filter-checkbox">
                            <label htmlFor={FILTER_IDS.paymentIssues}>
                                <input
                                    id={FILTER_IDS.paymentIssues}
                                    type="checkbox"
                                    checked={filters.paymentIssues}
                                    onChange={(event) => setFilter('paymentIssues', event.target.checked)}
                                />
                                Solo payment issues
                            </label>
                        </div>
                    </div>
                ) : null}

                <div className="aw-active-filters">
                    {activeFilterChips.map((chip) => (
                        <button
                            key={chip.key}
                            type="button"
                            className="aw-filter-chip"
                            onClick={() => clearFilter(chip.key)}
                            aria-label={`Rimuovi filtro ${chip.label}`}
                            title={`Rimuovi ${chip.label}`}
                        >
                            <span>{chip.label}</span>
                            <X size={12} />
                        </button>
                    ))}
                    <span className="aw-filter-hint">{advancedFilterCount} filtri avanzati attivi</span>
                </div>
            </div>

            <div className="aw-kpis-grid">
                <KpiCard label="Worker totali" value={formatNumber(kpis.workersTotal ?? 0)} />
                <KpiCard label="Worker con attività" value={formatNumber(kpis.workersWithActivity ?? 0)} />
                <KpiCard label="Connected Account utilizzati" value={formatNumber(kpis.connectedAccountsUsedBySoontip ?? 0)} />
                <KpiCard label="Connected Account fatturati da Stripe" value={formatNumber(kpis.connectedAccountsBilledByStripe ?? 0)} />
                <KpiCard
                    label="Costi Connect totali"
                    value={formatCurrencyFromCents(kpis.connectCostsTotalCents)}
                    subValue={connectCostsSubValue}
                    title="Card calcolata sul mese e sui filtri attivi"
                />
            </div>

            <div className="aw-table-toolbar">
                <div className="aw-page-size">
                    <label htmlFor="aw-page-size-select">Righe per pagina</label>
                    <select
                        id="aw-page-size-select"
                        value={filters.limit}
                        onChange={(event) => setFilters((prev) => ({ ...prev, limit: Number(event.target.value), page: 1 }))}
                    >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>

                <div className="aw-pagination-summary">
                    {totalRows === 0 ? '0–0 di 0 risultati' : `${startRow}–${endRow} di ${totalRows} risultati`}
                </div>
            </div>

            <div className="aw-table-wrapper">
                {loading ? (
                    <div className="aw-loading">
                        <Loader2 size={18} className="aw-spin" />
                        Caricamento dati Workers...
                    </div>
                ) : (
                    <table className="aw-table">
                        <colgroup>
                            <col className="aw-col-worker" />
                            <col className="aw-col-tenant" />
                            <col className="aw-col-activity" />
                            <col className="aw-col-money" />
                            <col className="aw-col-connect" />
                            <col className="aw-col-money" />
                            <col className="aw-col-money" />
                            <col className="aw-col-anomalies" />
                            <col className="aw-col-action" />
                        </colgroup>
                        <thead>
                            <tr>
                                <SortableHeader label="Worker" field="workerName" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                                <SortableHeader label="Tenant / Venue" field="tenantName" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                                <SortableHeader label="Attività" field="tipCount" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} alignRight />
                                <SortableHeader label="Netto worker" field="workerNetAmount" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} alignRight />
                                <SortableHeader label="Stato Connect" field="connectStatus" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                                <SortableHeader label="Volume payout" field="payoutVolume" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} alignRight />
                                <SortableHeader label="Costo Connect totale" field="totalConnectFee" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} alignRight />
                                <SortableHeader label="Anomalie" field="anomalies" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                                <th className="aw-th-right aw-action-th">Dettaglio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workers.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="aw-empty">{noResultsMessage}</td>
                                </tr>
                            ) : workers.map((row) => {
                                const issuesTitle = (row.paymentIssues || []).map((issue) => ISSUE_LABELS[issue] || issue).join('\n');
                                const availabilityMeta = CONNECT_DATA_STATE_META[row.stripeConnectCost?.dataState] || CONNECT_DATA_STATE_META.NOT_AVAILABLE;

                                return (
                                    <tr
                                        key={row.workerId}
                                        className="aw-table-row"
                                        tabIndex={0}
                                        onClick={() => openDrawer(row)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                openDrawer(row);
                                            }
                                        }}
                                    >
                                        <td className="aw-sticky-left aw-identity-cell">
                                            <div className="aw-worker-cell">
                                                <strong>{row.workerName}</strong>
                                                <span>{row.workerEmail || '—'}</span>
                                            </div>
                                        </td>
                                        <td className="aw-identity-cell">
                                            <div className="aw-tenant-venue-cell">
                                                <strong>{row.tenantName || '—'}</strong>
                                                <span>{row.venueName || '—'}</span>
                                            </div>
                                        </td>
                                        <td className="aw-cell-right">
                                            <div className="aw-activity-cell">
                                                <strong>{formatNumber(row.activity?.distinctTipsCount ?? 0)}</strong>
                                                <span>{formatShortDate(row.activity?.lastActivityAt)}</span>
                                            </div>
                                        </td>
                                        <td className="aw-cell-right">{formatCurrencyFromCents(row.activity?.netAmountCents)}</td>
                                        <td><ConnectStatusBadge status={row.connectStatus} /></td>
                                        <td className="aw-cell-right">{formatCurrencyFromCents(row.stripeConnectCost?.payoutVolumeCents)}</td>
                                        <td className="aw-cell-right">{formatCurrencyFromCents(row.stripeConnectCost?.totalConnectFeeCents)}</td>
                                        <td>
                                            <IssueBadge issues={row.paymentIssues || []} />
                                            {issuesTitle ? <div className="aw-cell-help" title={issuesTitle}>Dettaglio disponibile</div> : null}
                                            <div className="aw-cell-status-stack">
                                                <ConnectDataStateBadge state={row.stripeConnectCost?.dataState} />
                                                {row.stripeConnectCost?.lastSyncedAt ? (
                                                    <span className="aw-date-xs" title={availabilityMeta.title} aria-label={availabilityMeta.title}>
                                                        {formatDate(row.stripeConnectCost?.lastSyncedAt)}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className="aw-sticky-right aw-action-cell">
                                            <button
                                                type="button"
                                                className="aw-btn aw-btn-secondary aw-btn-compact"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    openDrawer(row);
                                                }}
                                                aria-label={`Apri dettaglio di ${row.workerName}`}
                                            >
                                                <Eye size={14} />
                                                Dettaglio
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="aw-pagination">
                <button
                    className="aw-btn aw-btn-secondary"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                >
                    Precedente
                </button>
                <span>
                    Pagina {currentPage} / {pagination.totalPages || 1}
                </span>
                <button
                    className="aw-btn aw-btn-secondary"
                    disabled={currentPage >= (pagination.totalPages || 1) || loading}
                    onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                >
                    Successiva
                </button>
            </div>

            {selectedWorker ? (
                <div className="aw-drawer-overlay" onClick={closeDrawer} aria-hidden="true" />
            ) : null}

            {selectedWorker ? (
                <aside className="aw-drawer" role="dialog" aria-modal="true" aria-labelledby="aw-drawer-title">
                    <div className="aw-drawer-header">
                        <div>
                            <h2 id="aw-drawer-title">{selectedWorker.workerName}</h2>
                            <p>{selectedWorker.workerEmail || '—'}</p>
                        </div>
                        <button
                            ref={closeButtonRef}
                            type="button"
                            className="aw-drawer-close"
                            onClick={closeDrawer}
                            aria-label="Chiudi dettaglio worker"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="aw-drawer-body">
                        <div className="aw-drawer-section">
                            <h3>Identità e contesto</h3>
                            <div className="aw-drawer-grid">
                                <DrawerMetric label="Tenant" value={selectedWorker.tenantName || '—'} />
                                <DrawerMetric label="Venue" value={selectedWorker.venueName || '—'} />
                                <DrawerMetric label="Mese" value={filters.yearMonth} />
                                <DrawerMetric label="Stato worker" value={<WorkerStatusBadge status={selectedWorker.workerStatus} />} />
                                <DrawerMetric label="Stato Connect" value={<ConnectStatusBadge status={selectedWorker.connectStatus} />} />
                                <DrawerMetric
                                    label="Connected Account"
                                    value={(
                                        <span className="aw-connected-account-value">
                                            <span>{maskAccountId(selectedWorker.providerAccountId)}</span>
                                            {selectedWorker.providerAccountId ? (
                                                <a
                                                    className="aw-connected-account-link"
                                                    href={`https://dashboard.stripe.com/connect/accounts/${encodeURIComponent(selectedWorker.providerAccountId)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Apri Connected Account su Stripe"
                                                    aria-label={`Apri Connected Account ${selectedWorker.providerAccountId} su Stripe`}
                                                >
                                                    <ExternalLink size={14} aria-hidden="true" />
                                                </a>
                                            ) : null}
                                        </span>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="aw-drawer-section">
                            <h3>Attività e costi</h3>
                            <div className="aw-drawer-grid aw-drawer-grid-metrics">
                                <DrawerMetric label="Numero mance" value={formatNumber(selectedWorker.activity?.distinctTipsCount ?? 0)} />
                                <DrawerMetric label="Ultima attività" value={formatDate(selectedWorker.activity?.lastActivityAt)} />
                                <DrawerMetric label="Netto worker" value={formatCurrencyFromCents(selectedWorker.activity?.netAmountCents)} />
                                <DrawerMetric label="Numero payout" value={formatNumber(selectedWorker.stripeConnectCost?.numberOfPayouts)} subValue={selectedWorker.stripeConnectCost?.numberOfPayouts == null ? 'Dato non ancora sincronizzato' : null} />
                                <DrawerMetric label="Volume payout" value={formatCurrencyFromCents(selectedWorker.stripeConnectCost?.payoutVolumeCents)} />
                                <DrawerMetric label="Active Account Billing" value={formatCurrencyFromCents(selectedWorker.stripeConnectCost?.activeAccountBillingCents)} />
                                <DrawerMetric label="Account Volume Billing" value={formatCurrencyFromCents(selectedWorker.stripeConnectCost?.accountVolumeBillingCents)} />
                                <DrawerMetric label="Payout Fee" value={formatCurrencyFromCents(selectedWorker.stripeConnectCost?.payoutFeeCents)} />
                                <DrawerMetric label="Verification Billing" value={formatCurrencyFromCents(selectedWorker.stripeConnectCost?.verificationBillingCents)} />
                                <DrawerMetric label="Costo Connect totale" value={formatCurrencyFromCents(selectedWorker.stripeConnectCost?.totalConnectFeeCents)} />
                                <DrawerMetric label="Stato del dato Stripe" value={<ConnectDataStateBadge state={selectedWorker.stripeConnectCost?.dataState} />} />
                                <DrawerMetric label="Ultima sincronizzazione" value={formatDate(selectedWorker.stripeConnectCost?.lastSyncedAt)} />
                            </div>
                        </div>

                        <div className="aw-drawer-section">
                            <h3>Payment issues</h3>
                            {selectedWorker.paymentIssues?.length ? (
                                <div className="aw-issue-list">
                                    {selectedWorker.paymentIssues.map((issue) => (
                                        <span key={issue} className="aw-issue-chip">
                                            {ISSUE_LABELS[issue] || issue}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <div className="aw-detail-empty">Nessuna anomalia rilevata.</div>
                            )}
                        </div>

                        <div className="aw-drawer-section">
                            <h3>Disponibilità dato</h3>
                            <div className="aw-detail-empty">
                                {selectedWorker.stripeConnectCost?.dataState === 'AVAILABLE'
                                    ? 'Dato disponibile per il mese selezionato.'
                                    : selectedWorker.stripeConnectCost?.dataState === 'NO_ACCOUNT'
                                        ? 'Worker senza Connected Account: il costo Connect non è applicabile.'
                                        : selectedWorker.stripeConnectCost?.dataState === 'SYNC_FAILED'
                                            ? 'Sincronizzazione fallita: consultare il banner di stato in pagina.'
                                            : 'Dato Stripe non ancora sincronizzato o non disponibile per il mese selezionato.'}
                            </div>
                        </div>
                    </div>
                </aside>
            ) : null}
        </div>
    );
}
